import { useState, useEffect, useRef } from 'react';
import { ChatCircleDots, PaperPlaneTilt, Paperclip, Lock } from '@phosphor-icons/react';
import Navigation from '@/components/Navigation';
import { API } from '@/App';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const Messages = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Whether the *latest* message between users is post-payment determines unlock state.
  const isPostPayment = messages.some((m) => m.is_post_payment);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await fetch(`${API}/auth/me`, { credentials: 'include' });
        const userData = await userRes.json();
        setCurrentUser(userData);

        const convRes = await fetch(`${API}/messages/conversations`, { credentials: 'include' });
        const convData = await convRes.json();
        setConversations(convData);
        setLoading(false);
      } catch (error) {
        toast.error('Failed to load conversations');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const loadMessages = async (otherUserId) => {
    try {
      const response = await fetch(`${API}/messages/${otherUserId}`, { credentials: 'include' });
      const data = await response.json();
      setMessages(data);
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv.other_user);
    loadMessages(conv.other_user.user_id);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      const response = await fetch(`${API}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          receiver_user_id: selectedConversation.user_id,
          message_content: newMessage
        })
      });

      if (!response.ok) {
        const data = await response.json();
        const msg = typeof data.detail === 'string' ? data.detail : 'Failed to send message';
        toast.error(msg, { duration: 6000 });
        return;
      }

      setNewMessage('');
      loadMessages(selectedConversation.user_id);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleImageAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedConversation) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error('Only PNG / JPEG / WEBP images are allowed.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await fetch(
        `${API}/messages/upload?receiver_user_id=${encodeURIComponent(selectedConversation.user_id)}`,
        { method: 'POST', credentials: 'include', body: fd }
      );
      const upData = await up.json();
      if (!up.ok) {
        toast.error(typeof upData.detail === 'string' ? upData.detail : 'Upload failed.');
        return;
      }
      const send = await fetch(`${API}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          receiver_user_id: selectedConversation.user_id,
          message_content: '[image]',
          attachment_url: upData.attachment_url,
          attachment_type: 'image',
        }),
      });
      if (!send.ok) {
        const d = await send.json();
        toast.error(typeof d.detail === 'string' ? d.detail : 'Failed to send image.');
        return;
      }
      loadMessages(selectedConversation.user_id);
    } catch (e) {
      toast.error('Image send failed.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6D28D9]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1
          className="text-4xl font-extrabold text-gray-900 mb-8"
          style={{ fontFamily: 'Outfit, sans-serif' }}
          data-testid="messages-title"
        >
          Messages
        </h1>

        <div className="grid md:grid-cols-3 gap-6 h-[600px]">
          <div className="bg-white rounded-xl border border-gray-200 overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h2 className="font-bold text-gray-900">Conversations</h2>
            </div>
            {conversations.length === 0 ? (
              <div className="p-8 text-center" data-testid="no-conversations">
                <ChatCircleDots size={48} weight="duotone" className="text-gray-500 mx-auto mb-4" />
                <p className="text-gray-600 text-sm">No conversations yet</p>
              </div>
            ) : (
              <div>
                {conversations.map((conv) => (
                  <button
                    key={conv.other_user.user_id}
                    onClick={() => handleSelectConversation(conv)}
                    className={`w-full p-4 border-b border-gray-200 hover:bg-gray-50 text-left transition-colors duration-200 ${
                      selectedConversation?.user_id === conv.other_user.user_id ? 'bg-[#6D28D9]/10' : ''
                    }`}
                    data-testid={`conversation-${conv.other_user.user_id}`}
                  >
                    <div className="flex items-center gap-3">
                      {conv.other_user.profile_image ? (
                        <img
                          src={conv.other_user.profile_image}
                          alt={conv.other_user.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-[#1E2A45] flex items-center justify-center">
                          <span className="text-gray-600 font-bold">
                            {conv.other_user.name?.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900 truncate">{conv.other_user.name}</p>
                        <p className="text-sm text-gray-500 truncate">
                          {conv.last_message.message_content}
                        </p>
                      </div>
                      {conv.unread_count > 0 && (
                        <span className="px-2 py-1 bg-[#6D28D9] text-gray-900 text-xs rounded-full">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="md:col-span-2 bg-white rounded-xl border border-gray-200 flex flex-col">
            {!selectedConversation ? (
              <div className="flex-1 flex items-center justify-center" data-testid="select-conversation">
                <div className="text-center">
                  <ChatCircleDots size={64} weight="duotone" className="text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-600">Select a conversation to start messaging</p>
                </div>
              </div>
            ) : (
              <>
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    {selectedConversation.profile_image ? (
                      <img
                        src={selectedConversation.profile_image}
                        alt={selectedConversation.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#1E2A45] flex items-center justify-center">
                        <span className="text-gray-600 font-bold">
                          {selectedConversation.name?.charAt(0)}
                        </span>
                      </div>
                    )}
                    <p className="font-bold text-gray-900">{selectedConversation.name}</p>
                    {isPostPayment ? (
                      <span className="ml-auto text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-green-100 text-green-700"
                            data-testid="chat-unlimited-badge">
                        Unlimited ✓
                      </span>
                    ) : (
                      <span className="ml-auto text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-800 flex items-center gap-1"
                            data-testid="chat-prepayment-badge">
                        <Lock size={10} weight="bold" /> Pre-payment
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="message-list">
                  {messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`flex ${msg.sender_user_id === currentUser?.user_id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs px-4 py-2 rounded-lg ${
                          msg.sender_user_id === currentUser?.user_id
                            ? 'bg-[#6D28D9] text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}
                      >
                        {msg.attachment_url && msg.attachment_type === 'image' && (
                          <img
                            src={msg.attachment_url.startsWith('http') ? msg.attachment_url : `${API.replace(/\/api$/, '')}${msg.attachment_url}`}
                            alt="attachment"
                            className="rounded-md mb-1 max-w-full"
                            data-testid={`msg-image-${msg.message_id}`}
                          />
                        )}
                        {msg.message_content && msg.message_content !== '[image]' && <p>{msg.message_content}</p>}
                        <p className="text-xs mt-1 opacity-70">
                          {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200">
                  <div className="flex gap-2 items-center">
                    {isPostPayment && (
                      <>
                        <input ref={fileInputRef} type="file"
                               accept="image/png,image/jpeg,image/webp"
                               className="hidden" onChange={handleImageAttachment} />
                        <button type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:text-[#6D28D9] hover:border-[#6D28D9] disabled:opacity-50"
                                data-testid="chat-attach-image">
                          <Paperclip size={18} weight="bold" />
                        </button>
                      </>
                    )}
                    <Input
                      type="text"
                      placeholder={isPostPayment ? "Type a message…" : "Pre-payment chat — no phone numbers / external contacts"}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      className="flex-1"
                      data-testid="message-input"
                    />
                    <Button
                      type="submit"
                      className="dropio-gradient-cta text-white rounded-full px-6"
                      data-testid="send-message"
                    >
                      <PaperPlaneTilt size={20} weight="bold" />
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;
